import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'

import { MainHeader } from './components/MainHeader'
import SkillPageSearchHeader from './components/SkillPageSearchHeader'
import { HeaderPage } from './constants/header.constants'
import { useBodyOverflowHidden } from './hooks/useBodyOverflowHidden'
import { useSearchNavigation } from './hooks/useSearchNavigation'
import { CommunityPage } from './pages/CommunityPage'
import { ResourcesPage } from './pages/ResourcesPage'
import { SkillListPage } from './pages/SkillListPage'
import {
  setExploreLearningsOnboarding,
  setExploreLearningsSkill,
  setExploreLearningsSkillNames,
  useExploreLearnings,
} from '../../store/ExploreLearnings'

export const ExploreLearnings = () => {
  const { section } = useParams<{ section: HeaderPage }>()
  const [currentPage, setCurrentPage] = useState<HeaderPage>(section || 'skills')
  const [searchOpen, setSearchOpen] = useState(false)
  const search = useExploreLearnings(state => state.search)
  const onboarding = useExploreLearnings(state => state.onboarding)
  const skillState = useExploreLearnings(state => state.skill)
  const prevSkillStateRef = useRef(skillState)
  const location = useLocation()
  const fromRoute = location.state?.from
  const isFromSkills = fromRoute?.startsWith('/skill')
  const { data: skillList } = useSkills(true)
  useBodyOverflowHidden(searchOpen)

  useEffect(() => {
    setCurrentPage(section || 'skills')
  }, [section])

  useEffect(() => {
    if (!search) return
    window.scrollTo(0, 0)
  }, [search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const onboardingParam = params.get('onboarding')
    const skillsParam = params.get('skills')
    const skillLabels = skillsParam ? skillsParam.split(',').map(s => s.trim()) : []

    if (onboardingParam === 'true' && onboarding !== 'true') {
      setExploreLearningsOnboarding('true')
    }

    if (skillLabels.length > 0 && skillList) {
      const skillListData = Array.isArray(skillList) ? skillList : skillList?.data
      const validSkills = skillLabels
        .map(label => skillListData.find((skill: any) => skill.name === label))
        .filter((skill: any): skill is any => !!skill)

      const validSkillIds = validSkills.map((skill: any) => skill.id)
      const validSkillNames = validSkills.map((skill: any) => skill.name)

      if (validSkillIds.length > 0 && JSON.stringify(validSkillIds) !== JSON.stringify(prevSkillStateRef.current)) {
        setExploreLearningsSkill(validSkillIds)
        setExploreLearningsSkillNames(validSkillNames)
      }
    }

    prevSkillStateRef.current = skillState
  }, [location.search, onboarding, skillState, skillList])

  const pageList = {
    skills: <SkillListPage />,
    resources: <ResourcesPage />,
    community: <CommunityPage />,
  }
  let navigate = useNavigate()

  const clearSkillHeaderState = () => {
    if (location.state?.from) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: undefined })
    }
  }

  const showSkillHeader = isFromSkills && !!search
  const handleNavigate = (page: HeaderPage) => {
    const params = new URLSearchParams(location.search)
    const search = params.toString()
    navigate(`/explore-learnings/${page}${search ? `?${search}` : ''}`, {
      replace: true,
      state: undefined,
    })
  }
  useSearchNavigation(currentPage)

  return (
    <div className="marketing-capabilities bg-[#fafafa]">
      {showSkillHeader ? (
        <SkillPageSearchHeader page={currentPage} search={search} />
      ) : (
        <MainHeader
          page={currentPage}
          setPage={handleNavigate}
          setSearchOpen={setSearchOpen}
          onSearchStart={clearSkillHeaderState}
        />
      )}
      <div className="max-w-[100vw] min-h-[80vh] overflow-hidden">
        {searchOpen && <div className="bg-[#191919] opacity-75 w-full h-full fixed top-0 left-0 z-10" />}
        {currentPage && pageList[currentPage]}
      </div>
    </div>
  )
}
